<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-flex-grid :rows="[['130px', '200px', '200px']]">
        <template #col-1-1><app-button @click.native="onShowModal">Create</app-button></template>
        <template #col-1-2>
          <app-select
            @input="onChangeFilter"
            v-model="legal_entity_filter"
            :options="legal_entity_options"
            :reduce="item => item.code"
            label="short_name"
            placeholder="Legal entity"
          />
        </template>
        <template #col-1-3>
          <app-select
            @input="onChangeFilter"
            v-model="tax_filter"
            :options="tax_options"
            :reduce="item => item.id"
            label="name_expense"
            placeholder="Tax"
          />
        </template>
      </app-flex-grid>
      <template v-if="table_pending_items.length">
        <app-cells><h2 class="title">Pending</h2></app-cells>
        <table-list-taxes-pending
          @clickPagination="onClickPaginationPending"
          :table-items="table_pending_items"
          :table-preloader="table_pending_preloader"
          :pagination="pagination_pending"
          :date-format="dateFormat"
          :date-format-month="dateFormatMonth"
          :amount-format="amountFormat"
        />
      </template>
      <template v-if="table_invoice_items.length">
        <app-cells><h2 class="title">Submitted. Waiting invoice</h2></app-cells>
        <table-list-taxes-invoice
          @clickPagination="onClickPaginationInvoice"
          :table-items="table_invoice_items"
          :table-preloader="table_invoice_preloader"
          :pagination="pagination_invoice"
          :date-format="dateFormat"
          :date-format-month="dateFormatMonth"
          :amount-format="amountFormat"
        />
      </template>
      <template v-if="table_payment_items.length">
        <app-cells><h2 class="title">Submitted. Waiting payment</h2></app-cells>
        <table-list-taxes-payment
          @clickPagination="onClickPaginationPayment"
          :table-items="table_payment_items"
          :table-preloader="table_payment_preloader"
          :pagination="pagination_payment"
          :date-format="dateFormat"
          :date-format-month="dateFormatMonth"
          :amount-format="amountFormat"
        />
      </template>
      <app-cells><h2 class="title">Completed</h2></app-cells>
      <table-list-taxes-completed
        @clickPagination="onClickPaginationCompleted"
        :table-items="table_completed_items"
        :table-preloader="table_completed_preloader"
        :pagination="pagination_completed"
        :date-format="dateFormat"
        :date-format-month="dateFormatMonth"
        :amount-format="amountFormat"
      />
    </template>
    <modal
      name="tax_modal"
      :width="800"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button class="popup__close" type="button" @click="$modal.hide('tax_modal')"></button>
        <div class="popup__title">Create tax</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-grid>
              <template #item-1>
                <app-form-group label="Legal entity" required>
                  <app-select
                    v-model="form.legal_entity"
                    :options="legal_entity_options"
                    :reduce="item => item.code"
                    :clearable="false"
                    label="short_name"
                    placeholder="Legal entity"
                    :error="$v.form.legal_entity.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.legal_entity.$dirty && !$v.form.legal_entity.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Tax" required>
                  <app-select
                    v-model="form.tax"
                    :options="tax_options"
                    :clearable="false"
                    label="name_expense"
                    placeholder="Tax"
                    :error="$v.form.tax.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.tax.$dirty && !$v.form.tax.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Date" required>
                  <app-date-picker
                    v-model="form.date"
                    :clearable="false"
                    placeholder="Date"
                    :error="$v.form.date.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.date.$dirty && !$v.form.date.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Accounting date" required>
                  <app-date-picker
                    v-model="form.accounting_date"
                    :clearable="false"
                    type="month"
                    format="MMM YYYY"
                    placeholder="Accounting date"
                    :error="$v.form.accounting_date.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.accounting_date.$dirty && !$v.form.accounting_date.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group label="Status" required class="form-group--mh">
                  <app-cells :indent="false">
                    <app-radio
                      v-for="radio of status_options"
                      :key="radio.name"
                      v-model="form.status"
                      :value="radio.code"
                      name="status"
                      :checked="form.status === radio.code"
                    >
                      <template #label>{{ radio.name }}</template>
                    </app-radio>
                  </app-cells>
                  <template #error>
                    <div v-if="$v.form.status.$dirty && !$v.form.status.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Amount" label-for="amount" :required="form.status === 'completed'">
                  <app-fin-number
                    v-model="form.amount"
                    id="amount"
                    :error="$v.form.amount.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.amount.$dirty && !$v.form.amount.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Attachment" :required="form.status === 'completed'">
                  <app-uploader
                    v-model="form.attachment"
                    :extensions="['application/pdf']"
                    formDataFormats="PDF"
                    class="custom-uploader--flex"
                  />
                  <template #error>
                    <div v-if="$v.form.attachment.$dirty && !$v.form.attachment.required">Required field</div>
                  </template>
                </app-form-group>
<!--                <app-form-group label="Behalf of" required class="form-group&#45;&#45;mh">-->
<!--                  <app-cells :indent="false">-->
<!--                    <app-radio-->
<!--                      v-for="radio of behalf_options"-->
<!--                      :key="radio.name"-->
<!--                      v-model="behalf_of"-->
<!--                      :value="radio.code"-->
<!--                      name="status"-->
<!--                      :checked="behalf_of === radio.code"-->
<!--                    >-->
<!--                      <template #label>{{ radio.name }}</template>-->
<!--                    </app-radio>-->
<!--                  </app-cells>-->
<!--                  <template #error>-->
<!--                    <div v-if="$v.form.status.$dirty && !$v.form.status.required">Required field</div>-->
<!--                  </template>-->
<!--                </app-form-group>-->
              </template>
            </app-grid>
            <app-form-group label="Additional notes">
              <app-textarea
                v-model="form.notes"
                rows="5"
                placeholder="Write a note"
                id="notes"
              />
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button type="submit" ref="submitTax">Save</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getAdministrativeLegalEntities,
  getFinanceTransactionsItemsList,
  getFinanceTaxesList,
  postFinanceTaxes,
  getFinanceTaxStatuses
} from '@/http'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { format, parse } from 'date-fns'
import TableListTaxesPending from '@/views/finance/taxes/taxes/tables/TableListTaxesPending'
import TableListTaxesInvoice from '@/views/finance/taxes/taxes/tables/TableListTaxesInvoice'
import TableListTaxesPayment from '@/views/finance/taxes/taxes/tables/TableListTaxesPayment'
import TableListTaxesCompleted from '@/views/finance/taxes/taxes/tables/TableListTaxesCompleted'

export default {
  name: 'FinanceTaxes',
  components: {TableListTaxesCompleted, TableListTaxesPending, TableListTaxesInvoice, TableListTaxesPayment},
  data() {
    return {
      page_loaded: false,
      tax_filter: null,
      legal_entity_filter: null,
      form: {},
      legal_entity_options: [],
      tax_options: [],
      status_options: [],
      behalf_options: [
        {code: 'supplier', name: 'Supplier'},
        {code: 'employees', name: 'Employees'},
      ],
      behalf_of: '',
      table_pending_items: [],
      pagination_pending: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_pending_preloader: false,
      table_invoice_items: [],
      pagination_invoice: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_invoice_preloader: false,
      table_payment_items: [],
      pagination_payment: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_payment_preloader: false,
      table_completed_items: [],
      pagination_completed: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_completed_preloader: false,
    }
  },
  validations: {
    form: {
      legal_entity: { required },
      tax: { required },
      date: { required },
      accounting_date: { required },
      status: { required },
      amount: { required: requiredIf(function () { return this.form.status === 'completed' }) },
      attachment: { required: requiredIf(function () { return this.form.status === 'completed' }) },
    }
  },
  created() {
    Promise.all([
      getAdministrativeLegalEntities(),
      getFinanceTransactionsItemsList({expense: true, groups: 'taxes,fees'}),
      getFinanceTaxStatuses(),
      this.fetchItemsPending(),
      this.fetchItemsInvoice(),
      this.fetchItemsPayment(),
      this.fetchItemsCompleted()
    ])
      .then(response => {
        this.legal_entity_options = response[0].data
        this.tax_options = response[1].data
        this.status_options = response[2].data
        this.page_loaded = true
      })
  },
  methods: {
    async fetchItemsPending() {
      this.table_pending_preloader = true
      const response = await getFinanceTaxesList({
        status: 'pending',
        page: this.pagination_pending.page,
        page_size: 20,
        legal_entity: this.legal_entity_filter,
        tax: this.tax_filter
      })
      this.table_pending_items = response.data.results
      this.pagination_pending.pages = response.data.pages
      this.pagination_pending.count = response.data.count
      this.table_pending_preloader = false
    },
    async fetchItemsInvoice() {
      this.table_invoice_preloader = true
      const response = await getFinanceTaxesList({
        status: 'submitted',
        invoice: false,
        page: this.pagination_invoice.page,
        page_size: 20,
        legal_entity: this.legal_entity_filter,
        tax: this.tax_filter
      })
      this.table_invoice_items = response.data.results
      this.pagination_invoice.pages = response.data.pages
      this.pagination_invoice.count = response.data.count
      this.table_invoice_preloader = false
    },
    async fetchItemsPayment() {
      this.table_payment_preloader = true
      const response = await getFinanceTaxesList({
        status: 'submitted',
        invoice: true,
        page: this.pagination_payment.page,
        page_size: 20,
        legal_entity: this.legal_entity_filter,
        tax: this.tax_filter
      })
      this.table_payment_items = response.data.results
      this.pagination_payment.pages = response.data.pages
      this.pagination_payment.count = response.data.count
      this.table_payment_preloader = false
    },
    async fetchItemsCompleted() {
      this.table_completed_preloader = true
      const response = await getFinanceTaxesList({
        status: 'completed',
        ordering: '-date',
        page: this.pagination_completed.page,
        page_size: 20,
        legal_entity: this.legal_entity_filter,
        tax: this.tax_filter
      })
      this.table_completed_items = response.data.results
      this.pagination_completed.pages = response.data.pages
      this.pagination_completed.count = response.data.count
      this.table_completed_preloader = false
    },
    fetchTables() {
      this.fetchItemsPending()
      this.fetchItemsInvoice()
      this.fetchItemsPayment()
      this.fetchItemsCompleted()
    },
    onClickPaginationPending(page) {
      this.pagination_pending.page = page
      this.fetchItemsPending()
    },
    onClickPaginationInvoice(page) {
      this.pagination_invoice.page = page
      this.fetchItemsInvoice()
    },
    onClickPaginationPayment(page) {
      this.pagination_payment.page = page
      this.fetchItemsPayment()
    },
    onClickPaginationCompleted(page) {
      this.pagination_completed.page = page
      this.fetchItemsCompleted()
    },
    onChangeFilter() {
      this.pagination_pending.page = 1
      this.pagination_invoice.page = 1
      this.pagination_payment.page = 1
      this.pagination_completed.page = 1
      this.fetchTables()
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submitTax.preload = true
      const data = {...this.form}
      data.tax = data.tax.id
      if (data.attachment) data.attachment = data.attachment.id
      postFinanceTaxes(data)
        .then(() => {
          this.fetchTables()
          this.$notify({
            type: 'success',
            title: 'Tax added',
          })
          this.$modal.hide('tax_modal')
        })
        .catch(() => { this.$refs.submitTax.preload = false })
    },
    onShowModal() {
      this.form = {}
      this.$v.form.$reset()
      this.$modal.show('tax_modal')
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    dateFormatMonth(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'MMM yyyy') : '-'
    },
    amountFormat(amount) {
      return amount ? new Intl.NumberFormat('ru-RU').format(amount) : '-'
    },
  },
}
</script>